/**
 * The lines the adopt dialog shows about what an adoption stored (2026-10-01).
 *
 * Read from the adoption's own result: how many entries were stored, how many
 * rows of the file were not, and which forms' entries were stored without a
 * link to a form. An unlinked entry is left out by documents built from the
 * dictionary, so each such form is named. Pure, so it is tested from
 * functions/vitest.web.config.ts.
 *
 * The not-stored breakdown is read through notStoredBreakdown, the helper the
 * Data Dictionary card uses (dictionarySizeSummary.ts), so the two count alike.
 */
import { notStoredBreakdown } from './dictionarySizeSummary';

export interface AdoptStorageResult {
  entriesStored?: number;
  entriesNotStored?: number;
  entriesUnlinked?: number;
  unlinkedForms?: Array<{ formName: string; entries: number; formFound: boolean }>;
  /** Why rows were not stored, each counted once (2026-10-01). Absent from an older function. */
  notStoredReasons?: { notChosen: number; neededReview: number; couldNotBeUsed: number };
  /** Entries created from form questions the file does not cover (2026-10-10). */
  entriesDerivedFromForms?: number;
  /** Forms whose uncovered questions could not get an entry, with the builder's reason. */
  derivedBlockedForms?: Array<{ formName: string; reason: string }>;
}

const entries = (n: number) => (n === 1 ? "1 entry" : `${n} entries`);

export function adoptStorageLines(r: AdoptStorageResult): string[] {
  const out: string[] = [];
  if (typeof r?.entriesStored !== "number") return out;
  out.push(`${entries(r.entriesStored)} stored as the study's data dictionary.`);
  const parts = notStoredBreakdown(r.entriesNotStored, r.notStoredReasons);
  const notStored = parts.total;
  if (notStored > 0) {
    const head = `${notStored === 1 ? "1 row in your file was" : `${notStored} rows in your file were`} not stored`;
    const why = r.notStoredReasons ? parts : null;
    if (!why) {
      out.push(`${head}.`);
    } else {
      out.push(`${head}:`);
      const was = (n: number) => (n === 1 ? "1 was" : `${n} were`);
      if (why.notChosen > 0) out.push(`${was(why.notChosen)} not chosen for this adoption`);
      if (why.neededReview > 0) {
        out.push(`${why.neededReview} needed review and ${why.neededReview === 1 ? "was" : "were"} not accepted`);
      }
      if (why.couldNotBeUsed > 0) out.push(`${why.couldNotBeUsed} could not be used`);
      // The card names these the same way (dictionarySizeSummary.ts).
      if (why.unexplained > 0) out.push(`${why.unexplained} for a reason not recorded`);
    }
  }
  const unlinked = r.entriesUnlinked ?? 0;
  if (unlinked > 0) {
    const forms = Array.isArray(r.unlinkedForms) ? r.unlinkedForms : [];
    const verb = unlinked === 1 ? "is" : "are";
    const lead = `${entries(unlinked)} ${verb} not linked to a form, so documents built from the dictionary may leave them out`;
    if (forms.length === 0) {
      out.push(`${lead}.`);
    } else {
      out.push(`${lead}:`);
      for (const f of forms) {
        const why = !f.formFound
          ? "whose form is not in the study"
          : f.entries === 1 ? "whose field is not on that form" : "whose fields are not on that form";
        out.push(`"${f.formName}": ${entries(f.entries)} ${why}`);
      }
    }
  }
  const derived = r.entriesDerivedFromForms ?? 0;
  if (derived > 0) {
    out.push(derived === 1
      ? "1 entry was created from the case report forms for a question the file does not cover."
      : `${derived} entries were created from the case report forms for questions the file does not cover.`);
  }
  const blocked = Array.isArray(r.derivedBlockedForms) ? r.derivedBlockedForms : [];
  if (blocked.length > 0) {
    out.push("For these forms no entries could be created for the questions the file does not cover, so those questions are missing from the data dictionary:");
    for (const b of blocked) {
      const why = (b.reason || "").trim().replace(/[.:]+$/, "")
        || "could not be built from the form; generate the data dictionary or fix the form";
      out.push(`"${b.formName}": ${why}`);
    }
  }
  return out;
}
